import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Spin, Table, Select, Space, Tag, Alert, Tooltip,
  Segmented, Empty,
} from 'antd';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as ChartTooltip,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../libs/api';

/**
 * PPA projects that have been signed but not commissioned.
 *
 * They cannot appear on the Commissioned Systems tab, which filters on a
 * commission date range — these have no commission date, because the date is
 * written when the system is commissioned and at no other time. So 981 PPA
 * projects had no entry anywhere in the page.
 *
 * Deliberately not a revenue forecast. 58% of what is here has sat in the same
 * stage for over two years and only 47 projects moved at all in the last 90
 * days, so presenting $8.4M as work in hand would be the more flattering
 * reading and the wrong one. Age leads.
 */

const AGE_BANDS = [
  { label: 'Under 90 days', min: 0, max: 90, color: '#05A677' },
  { label: '90 days – 1 year', min: 90, max: 365, color: '#F5A623' },
  { label: '1 – 2 years', min: 365, max: 730, color: '#FA8C16' },
  { label: 'Over 2 years', min: 730, max: Infinity, color: '#FA5252' },
];

/**
 * A project that has not moved in six months is not a live pipeline.
 *
 * Only 59 of the 665 active projects have moved inside that window; 386 have
 * sat in the same stage for over two years. Showing all of them by default
 * presents $8.4M as work in hand when almost none of it is moving, so the
 * stalled ones are set aside and counted in plain sight rather than dropped.
 */
const STALE_AFTER_DAYS = 180;

const STAGE_COLOR = {
  Agreement: 'blue',
  'Shipping & Installation': 'cyan',
  'Failed after signing': 'red',
};

const PIE_COLORS = ['#54A2CC', '#05A677', '#f5b759', '#845EC2', '#FA5252',
  '#0081CF', '#FF6F91', '#FFC75F', '#B0BEC5'];

/**
 * Past seven names the slices are too thin to read, so the tail is summed.
 *
 * It carries its own count rather than sitting there as a bare "Other": a
 * pipeline spread across thirty agents and one where two agents hold nearly
 * everything look identical once the tail is folded in.
 */
const TOP_SLICES = 7;

/**
 * Money, short.
 *
 * The cards were printing $794,892.6799999999 — a float handed to a Statistic
 * with no precision set, so every rounding error in the sum came out on the
 * page. Two decimals everywhere, and a k / M suffix past a thousand, so a card
 * and a table cell read the same way.
 */
const money = (v) => {
  const n = Number(v);
  if (v == null || !Number.isFinite(n)) return '-';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}k`;
  return `${sign}$${abs.toFixed(2)}`;
};

const count = (v) => Number(v || 0).toLocaleString();

function bandOf(days) {
  if (days == null) return null;
  return AGE_BANDS.find((b) => days >= b.min && days < b.max) || null;
}

/**
 * One breakdown, cut by whichever measure is selected.
 *
 * Projects and contracted value do not rank the same way — an agent with many
 * small jobs leads one and trails the other — so the sort follows the measure
 * on screen, and the tail is folded after sorting rather than before.
 */
function slices(list, keyFn, metric) {
  const map = new Map();
  for (const p of list) {
    const name = keyFn(p) || 'Unknown';
    map.set(name, (map.get(name) || 0) + (metric === 'value' ? (p.systemCost || 0) : 1));
  }
  const all = [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
  if (all.length <= TOP_SLICES + 1) return all;
  const tail = all.slice(TOP_SLICES);
  return [
    ...all.slice(0, TOP_SLICES),
    { name: `Other (${tail.length})`, value: tail.reduce((a, s) => a + s.value, 0) },
  ];
}

function SlicePie({ data, metric, height = 260 }) {
  const fmt = metric === 'value' ? money : count;
  if (!data.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing to show" />;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="45%"
          outerRadius={80}
          fontSize={11}
          label={({ name, percent }) => (percent >= 0.05
            ? `${name} ${(percent * 100).toFixed(0)}%` : '')}
        >
          {data.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <ChartTooltip formatter={(v, n) => [fmt(v), n]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function PpaPipelineTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(null);
  const [agent, setAgent] = useState(null);
  const [years, setYears] = useState(null);
  const [state, setState] = useState(null);
  const [includeStale, setIncludeStale] = useState(false);
  const [metric, setMetric] = useState('projects');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/ppa/pipeline', { timeout: 120000 });
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the pipeline');
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const inWindow = useCallback(
    (p) => includeStale || p.daysInStage == null || p.daysInStage <= STALE_AFTER_DAYS,
    [includeStale],
  );

  const rows = useMemo(() => {
    const all = data?.projects || [];
    return all.filter((p) => inWindow(p)
      && (!stage || p.status === stage)
      && (!agent || p.agentName === agent)
      && (!years || String(p.agreementYears) === String(years))
      && (!state || p.state === state));
  }, [data, stage, agent, years, state, inWindow]);

  /**
   * Everything above the table describes exactly what the table is showing.
   *
   * The cards used to come straight from the server, which counts the whole
   * book: with the stalled projects hidden, or an agent selected, they
   * described a set the table was not showing — the kind of disagreement nobody
   * notices until a number is quoted out of the page. Failed deals are the one
   * exclusion, and they are named in the note at the top.
   */
  const activeRows = useMemo(() => rows.filter((p) => p.statusCode !== 6), [rows]);

  const totals = useMemo(() => activeRows.reduce((a, p) => ({
    projects: a.projects + 1,
    sysKw: a.sysKw + (p.sysKw || 0),
    battKwh: a.battKwh + (p.battKwh || 0),
    systemCost: a.systemCost + (p.systemCost || 0),
    annualFee: a.annualFee + (p.annualFee || 0),
  }), { projects: 0, sysKw: 0, battKwh: 0, systemCost: 0, annualFee: 0 }), [activeRows]);

  const stalled = useMemo(() => {
    const active = (data?.projects || []).filter((p) => p.statusCode !== 6);
    const old = active.filter((p) => p.daysInStage > STALE_AFTER_DAYS);
    return {
      count: old.length,
      value: old.reduce((a, p) => a + (p.systemCost || 0), 0),
      total: active.length,
    };
  }, [data]);

  const byState = useMemo(() => slices(activeRows, (p) => p.state, metric), [activeRows, metric]);
  const byAgent = useMemo(() => slices(activeRows, (p) => p.agentName, metric), [activeRows, metric]);

  /**
   * The counts beside each filter describe what that filter can reach.
   *
   * They came from the server, which tallies the whole book, so the Stage list
   * offered "Agreement (563)" while the page was showing 62 projects — and a
   * name that only exists outside the six-month window emptied the table when
   * picked. Tallied over the set the table is drawn from instead, and names
   * with nothing behind them are left out rather than offered: "Unassigned"
   * and "Unspecified" were options that could never match anything, since the
   * filter compares them against a null agent and a null term.
   */
  const facets = useMemo(() => {
    const base = (data?.projects || []).filter(inWindow);
    const tally = (keyFn) => {
      const m = new Map();
      for (const p of base) {
        const k = keyFn(p);
        if (k == null || k === '') continue;
        m.set(k, (m.get(k) || 0) + 1);
      }
      return [...m.entries()].map(([name, projects]) => ({ name, projects }));
    };
    const byCount = (a, b) => b.projects - a.projects;
    return {
      stage: tally((p) => p.status).sort(byCount),
      agent: tally((p) => p.agentName).sort(byCount),
      state: tally((p) => p.state).sort(byCount),
      years: tally((p) => (p.agreementYears ? String(p.agreementYears) : null))
        .sort((a, b) => Number(a.name) - Number(b.name)),
    };
  }, [data, inWindow]);

  const options = (list) => (list || []).map((x) => ({ label: `${x.name} (${x.projects})`, value: x.name }));

  const columns = [
    { title: 'Project No', dataIndex: 'projectNo', width: 165, ellipsis: true, fixed: 'left' },
    { title: 'Client', dataIndex: 'clientName', width: 170, ellipsis: true, fixed: 'left' },
    {
      title: 'Stage',
      dataIndex: 'status',
      width: 170,
      render: (v) => <Tag color={STAGE_COLOR[v]}>{v}</Tag>,
    },
    {
      title: 'In stage',
      dataIndex: 'daysInStage',
      width: 130,
      align: 'right',
      sorter: (a, b) => (a.daysInStage || 0) - (b.daysInStage || 0),
      defaultSortOrder: 'descend',
      render: (v, r) => {
        if (v == null) return '-';
        const band = bandOf(v);
        const since = r.stageSince ? dayjs(r.stageSince).format('MMM YYYY') : null;
        return (
          <Tooltip title={since ? `Entered this stage ${since}` : undefined}>
            <span style={{ color: band?.color }}>
              {v >= 365 ? `${(v / 365).toFixed(1)} yr` : `${v} d`}
            </span>
          </Tooltip>
        );
      },
    },
    { title: 'Agent', dataIndex: 'agentName', width: 170, ellipsis: true, render: (v) => v || '-' },
    { title: 'State', dataIndex: 'state', width: 70, render: (v) => v || '-' },
    {
      title: 'Years', dataIndex: 'agreementYears', width: 70, align: 'right',
      render: (v) => v || '-',
    },
    {
      title: 'Sys kW', dataIndex: 'sysKw', width: 85, align: 'right',
      render: (v) => (v ? v.toFixed(1) : '-'),
      sorter: (a, b) => (a.sysKw || 0) - (b.sysKw || 0),
    },
    {
      title: 'Batt kWh', dataIndex: 'battKwh', width: 90, align: 'right',
      render: (v) => (v ? v.toFixed(1) : '-'),
      sorter: (a, b) => (a.battKwh || 0) - (b.battKwh || 0),
    },
    {
      title: 'Contracted', dataIndex: 'systemCost', width: 110, align: 'right',
      render: money,
      sorter: (a, b) => (a.systemCost || 0) - (b.systemCost || 0),
    },
    {
      title: 'Monthly Fee', dataIndex: 'monthlyFee', width: 110, align: 'right',
      render: money,
      sorter: (a, b) => (a.monthlyFee || 0) - (b.monthlyFee || 0),
    },
  ];

  const t = totals;
  const f = data?.failed;

  return (
    <Spin spinning={loading}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Signed but not yet commissioned"
        description={(
          <span>
            These projects have no commission date, so they cannot appear on the Commissioned
            Systems tab. Every figure here is contracted rather than incurred — nothing has been
            built, so there is no agent bill to read and no actual cost to show.
            {f?.projects > 0 && (
              <>
                {' '}
                The {count(f.projects)} that were signed and then failed ({money(f.systemCost)})
                {' '}sit in the table under their own stage, but out of every figure above it:
                folding them in would overstate what is still coming.
              </>
            )}
          </span>
        )}
      />

      {stalled.count > 0 && (
        <Alert
          type={includeStale ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={includeStale
            ? `Showing everything, including ${count(stalled.count)} projects that have not moved in six months`
            : `${count(stalled.count)} of ${count(stalled.total)} projects are hidden — nothing has moved on them in six months`}
          description={(
            <span>
              {includeStale
                ? `The ${money(stalled.value)} behind them is contracted, not expected: a project `
                  + 'sitting in the same stage for months is a question for someone, not a forecast.'
                : `They hold ${money(stalled.value)} of contracted value between them. Left in, they `
                  + 'would make the pipeline look several times larger than what is actually moving.'}
              {' '}
              <a
                href="#toggle-stale"
                onClick={(e) => { e.preventDefault(); setIncludeStale(!includeStale); }}
              >
                {includeStale ? 'Hide them again' : 'Show them anyway'}
              </a>
            </span>
          )}
        />
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title={includeStale ? 'Active projects' : 'Moving in the last 6 months'}
              value={t.projects}
            />
          </Card>
        </Col>
        <Col span={5}><Card size="small"><Statistic title="Contracted value" value={money(t.systemCost)} /></Card></Col>
        <Col span={5}><Card size="small"><Statistic title="Annual fee if all commission" value={money(t.annualFee)} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="Solar" value={t.sysKw} suffix="kW" precision={1} /></Card></Col>
        <Col span={5}><Card size="small"><Statistic title="Battery" value={t.battKwh} suffix="kWh" precision={1} /></Card></Col>
      </Row>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title="Where the active pipeline sits"
        extra={(
          <Segmented
            size="small"
            value={metric}
            onChange={setMetric}
            options={[
              { label: 'Projects', value: 'projects' },
              { label: 'Contracted value', value: 'value' },
            ]}
          />
        )}
      >
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>By state</div>
            <SlicePie data={byState} metric={metric} />
          </Col>
          <Col xs={24} lg={12}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>By agent</div>
            <SlicePie data={byAgent} metric={metric} />
          </Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <Select allowClear placeholder="Stage" style={{ width: 210 }} size="small"
            value={stage} onChange={setStage} options={options(facets.stage)} />
          <Select allowClear placeholder="Agent" style={{ width: 230 }} size="small"
            showSearch optionFilterProp="label"
            value={agent} onChange={setAgent} options={options(facets.agent)} />
          <Select allowClear placeholder="Agreement years" style={{ width: 180 }} size="small"
            value={years} onChange={setYears}
            options={facets.years.map((y) => ({
              label: `${y.name} year (${y.projects})`, value: y.name,
            }))} />
          <Select allowClear placeholder="State" style={{ width: 150 }} size="small"
            value={state} onChange={setState} options={options(facets.state)} />
          <span style={{ color: '#888', fontSize: 12 }}>
            {count(rows.length)} of {count((data?.projects || []).length)} shown
          </span>
        </Space>
      </Card>

      <Table
        rowKey="projectId"
        columns={columns}
        dataSource={rows}
        size="small"
        scroll={{ x: 1400 }}
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (n) => `${count(n)} projects` }}
      />
    </Spin>
  );
}
